import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { useSettings, digits } from "@/context/SettingsContext";
import { FAQS, fillFaq } from "@/data/faqs";
import { ChevronDown, MessageCircle, Mail, Phone } from "lucide-react";

export default function Faq() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(0);
  const wa = digits(settings.whatsapp_number);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: fillFaq(f.a, settings) },
    })),
  };

  return (
    <div className="container-x py-12 md:py-16 max-w-3xl" data-testid="faq-page">
      <SEO
        title="Frequently Asked Questions"
        description="Answers about UPI payments, all-India & worldwide delivery, order confirmation, tracking, packaging, cancellations and how to contact Adiyogi Handicrafts."
        schema={faqSchema}
      />

      <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Help Centre</div>
      <h1 className="font-serif text-3xl md:text-5xl mb-3">Frequently Asked Questions</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-8 max-w-xl">
        Everything you need to know about ordering, payments, shipping and our handcrafted collections.
      </p>

      <div className="border hairline rounded-xl bg-white overflow-hidden divide-y divide-[rgba(184,134,11,0.15)]">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} data-testid={`faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-4 text-left px-4 md:px-6 py-4 md:py-5 hover:bg-muted/40 transition-colors"
                data-testid={`faq-q-${i}`}
              >
                <span className="font-serif text-base md:text-lg leading-snug">{f.q}</span>
                <ChevronDown
                  className={`w-5 h-5 shrink-0 gold-text transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  strokeWidth={1.5}
                />
              </button>
              <div
                className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <div className="overflow-hidden">
                  <p className="px-4 md:px-6 pb-5 text-sm md:text-base text-muted-foreground leading-relaxed" data-testid={`faq-a-${i}`}>
                    {fillFaq(f.a, settings)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Still need help — dynamic contact strip */}
      <div className="mt-10 border hairline rounded-xl p-5 md:p-6 bg-muted/30 text-center" data-testid="faq-contact-strip">
        <div className="font-serif text-xl md:text-2xl mb-2">Still have a question?</div>
        <p className="text-sm text-muted-foreground mb-4">We usually reply within a few hours on business days.</p>
        <div className="flex flex-wrap justify-center gap-2.5">
          {wa && (
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="rounded-full bg-emerald-600 text-white px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-emerald-700" data-testid="faq-wa-cta">
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp Us
            </a>
          )}
          {settings.phone && (
            <a href={`tel:${String(settings.phone).replace(/[^+\d]/g, "")}`} className="btn-outline-gold rounded-full px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="faq-call-cta">
              <Phone className="w-4 h-4" strokeWidth={1.5} /> {settings.phone}
            </a>
          )}
          {settings.email && (
            <a href={`mailto:${settings.email}`} className="btn-outline-gold rounded-full px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="faq-email-cta">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Email Us
            </a>
          )}
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          Or use the <Link to="/contact" className="gold-text hover:underline">contact form</Link>.
        </div>
      </div>
    </div>
  );
}
